import { useState, useMemo, useEffect } from 'react';
import { Modal, Form, Button, Alert, Row, Col, Badge } from 'react-bootstrap';
import { redisFetch } from '../../features/jobs/api/http';

const PHASE_LABEL = {
  unwinding: 'Unwinding',
  pre_stranding: 'Pre-Stranding',
  conductor_isolation: 'Isolation',
  rope: 'Rope',
  interior_sheat: 'Interior Sheath',
  armouring: 'Armouring',
  exterior_sheat: 'Exterior Sheath',
  coiling: 'Coiling',
  rewinding: 'Rewinding'
};
const phaseLabel = p => PHASE_LABEL[p] || p || '-';
const fmt = n => (Number.isFinite(Number(n)) ? Number(n).toLocaleString('ro-RO') : '—');

// The target's merge group = the target job + every queue job that SHARES a token
// with it (merge-execution). Group-by jobs keep individual tokens, so they don't
// share and the group is just [target].
function mergeGroupOf(targetJobId, allJobs) {
  const target = (allJobs || []).find(j => String(j.jobId) === String(targetJobId));
  if (!target) return targetJobId ? [String(targetJobId)] : [];
  const tgtTokens = new Set(
    (target.queue || [])
      .flatMap(t => (t.outputs || []).map(o => o?.tokenId))
      .filter(Boolean)
  );
  const group = new Set([String(targetJobId)]);
  for (const j of allJobs || []) {
    if (String(j.jobId) === String(targetJobId)) continue;
    const shares = (j.queue || []).some(t =>
      (t.outputs || []).some(o => tgtTokens.has(o?.tokenId))
    );
    if (shares) group.add(String(j.jobId));
  }
  return [...group];
}

/**
 * Every drum currently queued on this machine, as a choosable target.
 *
 * This is the half that was missing. "Move to another product" always created a
 * NEW drum on the chosen product, which answers "this length belongs to another
 * product" — and not the question actually asked on the floor: "these 500 m
 * belong on THAT drum", the one already queued, already a colour, already a
 * bobbin somebody will mount. A new drum there is a second bobbin nobody wants.
 */
function queuedDrums(allJobs = []) {
  const out = [];
  for (const job of allJobs) {
    for (const task of job.queue || []) {
      const tokenId = (task.outputs || [])[0]?.tokenId || null;
      if (!tokenId) continue;
      const out0 = (task.outputs || [])[0] || {};
      out.push({
        key: `${job.jobId}::${task.taskId}::${tokenId}`,
        jobId: job.jobId,
        taskId: task.taskId,
        tokenId,
        product: job.productLabel || job.name || job.jobId,
        phase: task.phase,
        color: task.color ?? out0.color ?? null,
        status: String(task.status || out0.status || 'PENDING').toUpperCase(),
        plannedLength: Number(task.plannedLength || 0),
        producedLength: Number(out0.producedLength || 0),
        shared: (task.outputs || []).length > 0 && !!out0.shared
      });
    }
  }
  return out;
}

const DONE = new Set(['DONE', 'COMPLETED', 'FINISHED']);

// Move LENGTH (full or partial) off one or more source drums.
//
// Two kinds of destination:
//   · ONTO A QUEUED DRUM — the length is added to a drum that is already there.
//     No new bobbin, queue order untouched. This is the reconcile a planner
//     actually does while the queue is running.
//   · A NEW DRUM on a product — the original behaviour, kept because it is the
//     right answer when the metres belong to a product that has no drum for them
//     yet (shared across the target's merge group if it is a merge execution).
//
// Either way the source drum stays in place with its length reduced.
const MoveToProductModal = ({ show, onHide, block, allJobs = [], machineId, onDone }) => {
  const items = useMemo(
    () =>
      (block?.items || []).map(it => {
        const t = it.task || {};
        const g = it.group || {};
        const tokenId = (t.outputs || [])[0]?.tokenId || null;
        return {
          key: `${g.jobId}::${t.taskId}::${tokenId}`,
          sourceJobId: g.jobId,
          sourceProduct: g.productLabel || g.name || g.jobId,
          taskId: t.taskId,
          tokenId,
          phase: t.phase,
          color: t.color,
          plannedLength: Number(t.plannedLength || 0)
        };
      }),
    [block]
  );

  const [rows, setRows] = useState([]);
  const [mode, setMode] = useState('drum'); // 'drum' | 'product'
  const [targetDrumKey, setTargetDrumKey] = useState('');
  const [targetJobId, setTargetJobId] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (show) {
      setRows(items.map(i => ({ key: i.key, include: true, amount: i.plannedLength })));
      setMode('drum');
      setTargetDrumKey('');
      setTargetJobId('');
      setPreview(null);
      setErr(null);
    }
  }, [show, items]);

  const sourceKeys = useMemo(() => new Set(items.map(i => i.key)), [items]);
  const sourcePhases = useMemo(
    () => new Set(items.map(i => String(i.phase || ''))),
    [items]
  );

  // A finished drum cannot grow — its metres are against a real bobbin already —
  // and the phase has to match: a length of isolation is not a length of rope.
  const drumOptions = useMemo(
    () =>
      queuedDrums(allJobs).filter(
        d =>
          !sourceKeys.has(d.key) && !DONE.has(d.status) && sourcePhases.has(String(d.phase || ''))
      ),
    [allJobs, sourceKeys, sourcePhases]
  );

  const targetDrum = drumOptions.find(d => d.key === targetDrumKey) || null;

  const targetJobIds = useMemo(
    () => (mode === 'product' && targetJobId ? mergeGroupOf(targetJobId, allJobs) : []),
    [mode, targetJobId, allJobs]
  );
  const isMergeTarget = targetJobIds.length > 1;
  const targetProducts = targetJobIds.map(
    id => (allJobs || []).find(j => String(j.jobId) === String(id))?.productLabel || id
  );

  const setRow = (key, patch) =>
    setRows(rs => rs.map(r => (r.key === key ? { ...r, ...patch } : r)));

  const selected = rows
    .filter(r => r.include && Number(r.amount) > 0)
    .map(r => ({ ...r, meta: items.find(i => i.key === r.key) }));

  const movedTotal = selected.reduce((s, r) => s + Number(r.amount || 0), 0);
  const chosen = mode === 'drum' ? !!targetDrum : !!targetJobId;

  // Moving metres onto a drum of a different colour means those metres will be
  // made in THAT colour. Legitimate — it is what a reconcile is — but it has to
  // be said rather than discovered on the floor.
  const colourChange =
    mode === 'drum' &&
    targetDrum &&
    selected.some(
      s =>
        (s.meta?.color || null) &&
        targetDrum.color &&
        String(s.meta.color).toUpperCase() !== String(targetDrum.color).toUpperCase()
    );

  const run = async dryRun => {
    setErr(null);
    setBusy(true);
    try {
      const results = [];
      for (const s of selected) {
        const res = await redisFetch(`/api/redis/queues/${machineId}/move-length`, {
          method: 'POST',
          body: JSON.stringify({
            source: {
              jobId: s.meta.sourceJobId,
              taskId: s.meta.taskId,
              tokenId: s.meta.tokenId
            },
            ...(mode === 'drum'
              ? {
                  targetDrum: {
                    jobId: targetDrum.jobId,
                    taskId: targetDrum.taskId,
                    tokenId: targetDrum.tokenId
                  }
                }
              : { targetJobIds }),
            amount: Number(s.amount),
            dryRun
          })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
        results.push(data);
      }
      if (dryRun) setPreview(results);
      else onDone?.();
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  if (!block) return null;
  const targetOptions = (allJobs || []).filter(j => j.jobId);

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="fs-7">Move length off this drum</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="fs-9 fw-semibold text-700 mb-2">
          Source drum(s) — set how much to move (full length by default):
        </div>
        {items.map(i => {
          const r = rows.find(x => x.key === i.key) || {};
          const amount = Number(r.amount || 0);
          const left = Math.max(0, i.plannedLength - amount);
          return (
            <Row key={i.key} className="align-items-center g-2 mb-2">
              <Col xs="auto">
                <Form.Check
                  type="checkbox"
                  checked={!!r.include}
                  disabled={items.length === 1}
                  onChange={e => {
                    setRow(i.key, { include: e.target.checked });
                    setPreview(null);
                  }}
                />
              </Col>
              <Col>
                <span className="fs-9">
                  <b>{i.sourceProduct}</b> · {phaseLabel(i.phase)} · {i.color || '-'} ·{' '}
                  <span className="font-monospace text-700">{i.tokenId || '-'}</span>
                </span>
              </Col>
              <Col xs="auto" className="d-flex align-items-center gap-2">
                <Form.Control
                  size="sm"
                  type="number"
                  min={0}
                  max={i.plannedLength}
                  style={{ width: 100 }}
                  value={r.amount ?? ''}
                  disabled={!r.include}
                  onChange={e => {
                    setRow(i.key, { amount: e.target.value });
                    setPreview(null);
                  }}
                />
                <span className="fs-10 text-700">
                  m of {fmt(i.plannedLength)}
                  {r.include && amount > 0 && amount < i.plannedLength && (
                    <> · leaves {fmt(left)} m</>
                  )}
                </span>
              </Col>
            </Row>
          );
        })}

        {/* Partial moves are the normal case, so say the quick ways to set one. */}
        {items.length === 1 && (
          <div className="d-flex flex-wrap gap-1 mb-2">
            {[0.25, 0.5, 0.75, 1].map(f => (
              <Button
                key={f}
                size="sm"
                variant="phoenix-secondary"
                className="fs-10 py-0 px-2"
                onClick={() => {
                  setRow(items[0].key, {
                    amount: Math.round(items[0].plannedLength * f)
                  });
                  setPreview(null);
                }}
              >
                {f === 1 ? 'all' : `${f * 100}%`}
              </Button>
            ))}
          </div>
        )}

        <div className="fs-9 fw-semibold text-700 mt-3 mb-1">Move it to:</div>
        <div className="d-flex gap-3 mb-2">
          <Form.Check
            type="radio"
            id="move-mode-drum"
            label={<span className="fs-9">a drum already in the queue</span>}
            checked={mode === 'drum'}
            onChange={() => {
              setMode('drum');
              setPreview(null);
            }}
          />
          <Form.Check
            type="radio"
            id="move-mode-product"
            label={<span className="fs-9">a new drum on a product</span>}
            checked={mode === 'product'}
            onChange={() => {
              setMode('product');
              setPreview(null);
            }}
          />
        </div>

        {mode === 'drum' ? (
          <Form.Group className="mb-2">
            <Form.Select
              size="sm"
              value={targetDrumKey}
              onChange={e => {
                setTargetDrumKey(e.target.value);
                setPreview(null);
              }}
            >
              <option value="">— choose a queued drum —</option>
              {drumOptions.map(d => (
                <option key={d.key} value={d.key}>
                  {d.product} · {d.color || '—'} · {d.tokenId} · {fmt(d.plannedLength)} m ·{' '}
                  {d.status}
                </option>
              ))}
            </Form.Select>

            {!drumOptions.length && (
              <div className="text-warning fs-10 mt-1">
                No other unfinished drum of this phase is queued on{' '}
                {String(machineId).toUpperCase()} — use “a new drum on a product”.
              </div>
            )}

            {targetDrum && (
              <div className="fs-10 mt-2">
                <div>
                  <b>{targetDrum.product}</b> · {targetDrum.tokenId} goes from{' '}
                  <span className="font-monospace">{fmt(targetDrum.plannedLength)}</span> to{' '}
                  <span className="font-monospace fw-bold">
                    {fmt(targetDrum.plannedLength + movedTotal)}
                  </span>{' '}
                  m. No new drum, queue order untouched.
                </div>
                {targetDrum.status === 'ACTIVE' && (
                  <div className="text-warning mt-1">
                    That drum is RUNNING — its target length changes under the
                    operator, and on a machine that completes by length it will
                    run on to the new figure.
                  </div>
                )}
                {colourChange && (
                  <div className="text-warning mt-1">
                    Different colour: those metres will be made as{' '}
                    <b>{targetDrum.color}</b>.
                  </div>
                )}
              </div>
            )}
          </Form.Group>
        ) : (
          <Form.Group className="mb-2">
            <Form.Select
              size="sm"
              value={targetJobId}
              onChange={e => {
                setTargetJobId(e.target.value);
                setPreview(null);
              }}
            >
              <option value="">— choose target product —</option>
              {/* The LOT, not just the product: two lots of one product are the
                  normal case on a queue, and the id does not separate them —
                  the first bytes of an ObjectId are a timestamp, so two jobs
                  launched in the same second read identically at six
                  characters. */}
              {targetOptions.map(j => (
                <option key={j.jobId} value={j.jobId}>
                  {j.productLabel || j.name || j.jobId} · lot {fmt(j.plannedLength)} m (
                  {String(j.jobId).slice(-6)})
                </option>
              ))}
            </Form.Select>
            {isMergeTarget && (
              <div className="text-info fs-10 mt-1">
                Merge target → creates a shared drum in: <b>{targetProducts.join(', ')}</b>
              </div>
            )}
          </Form.Group>
        )}

        {err && (
          <Alert variant="danger" className="fs-9 mb-2">
            {err}
          </Alert>
        )}

        {preview && (
          <Alert variant="info" className="fs-9 mb-0">
            <div className="fw-semibold mb-1">Preview (nothing written yet):</div>
            {preview.map((p, i) => (
              <div key={i}>
                → move <b>{fmt(p.source?.movedAmount)} m</b> from <b>{p.source?.product}</b>{' '}
                (leaves {fmt(p.source?.remaining)} m){' '}
                {p.mode === 'ontoDrum' ? (
                  <>
                    onto <b>{p.target?.product}</b>{' '}
                    <span className="font-monospace">{p.target?.tokenId}</span> ·{' '}
                    {fmt(p.target?.before)} → <b>{fmt(p.target?.after)} m</b>
                  </>
                ) : (
                  <>
                    · new drum
                    {p.shared ? ' (shared)' : ''} at{' '}
                    <b>{(p.created || []).map(c => c.product).join(' + ')}</b>
                  </>
                )}
              </div>
            ))}
            <div className="text-700 fs-10 mt-1">
              {preview[0]?.mode === 'ontoDrum'
                ? 'The length is added to a drum that is already queued — nothing is appended.'
                : 'Queue order preserved; the source drum stays in place; new drums are appended.'}
            </div>
          </Alert>
        )}
      </Modal.Body>

      <Modal.Footer>
        {movedTotal > 0 && (
          <Badge bg="secondary" variant="phoenix" className="me-auto fs-10">
            {fmt(movedTotal)} m selected
          </Badge>
        )}
        <Button variant="link" className="text-600" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="outline-primary"
          disabled={busy || !chosen || !selected.length}
          onClick={() => run(true)}
        >
          {busy ? '...' : 'Preview'}
        </Button>
        <Button
          variant="primary"
          disabled={busy || !chosen || !selected.length || !preview}
          onClick={() => run(false)}
        >
          {busy ? 'Applying...' : 'Move'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MoveToProductModal;
